import { FormEvent, useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import { Alert, Badge, Button, Col, Input, Label, Row, Spinner } from "../../../Components/ui";
import {
  AlimTalkAccountResponse,
  AlimTalkAccountUpdatePayload,
  notificationCenterService,
} from "../../../api/services/notification-center.service";
import { dialogService } from "../../../utils/dialogService";

const getErrorMessage = (error: any, fallback: string) => {
  const responseData = error?.response?.data;
  if (typeof responseData?.detail === "string") return responseData.detail;
  if (typeof responseData?.error === "string") return responseData.error;
  if (Array.isArray(responseData?.errors) && responseData.errors.length) {
    return responseData.errors.join(", ");
  }
  if (responseData && typeof responseData === "object") {
    const firstFieldError = Object.values(responseData).find((value) => (
      typeof value === "string" || (Array.isArray(value) && value.length)
    ));
    if (typeof firstFieldError === "string") return firstFieldError;
    if (Array.isArray(firstFieldError)) return firstFieldError.join(", ");
  }
  return error?.message || fallback;
};

const getDeliveryStatus = (account: AlimTalkAccountResponse) => {
  if (account.delivery_ready) {
    return { label: "발송 준비 완료", color: "success" };
  }
  if (account.configured || account.sender_key_hint) {
    return { label: "설정 확인 필요", color: "warning" };
  }
  return { label: "연결 필요", color: "secondary" };
};

type AlimTalkAccountSettingsProps = {
  readOnly?: boolean;
};

export default function AlimTalkAccountSettings({ readOnly = false }: AlimTalkAccountSettingsProps) {
  const [account, setAccount] = useState<AlimTalkAccountResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [sendProfileKey, setSendProfileKey] = useState("");
  const [profileName, setProfileName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessNumber, setBusinessNumber] = useState("");
  const [senderNo, setSenderNo] = useState("");

  const applyAccount = useCallback((nextAccount: AlimTalkAccountResponse) => {
    setAccount(nextAccount);
    setSendProfileKey("");
    setProfileName(nextAccount.sender?.profile_name || "");
    setBusinessName(nextAccount.sender?.business_name || "");
    setBusinessNumber(nextAccount.sender?.business_number || "");
    setSenderNo(nextAccount.using_default ? "" : nextAccount.sender?.sender_no || "");
  }, []);

  const loadAccount = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await notificationCenterService.getAlimTalkAccount();
      applyAccount(response.data);
    } catch (error: any) {
      setLoadError(getErrorMessage(error, "알림톡 연결 정보를 불러오지 못했습니다."));
    } finally {
      setLoading(false);
    }
  }, [applyAccount]);

  useEffect(() => {
    void loadAccount();
  }, [loadAccount]);

  const saveAccount = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!profileName.trim() || !businessName.trim()) {
      toast.error("프로필명과 사업자명을 입력해 주세요.");
      return;
    }
    if (!senderNo.trim()) {
      toast.error("DK에 등록된 발신번호를 입력해 주세요.");
      return;
    }
    const hasExplicitSiteKey = Boolean(
      account && !account.using_default && account.sender_key_hint,
    );
    if (!hasExplicitSiteKey && !sendProfileKey.trim()) {
      toast.error("이 지점에서 사용할 발신 프로필 키를 입력해 주세요.");
      return;
    }

    const payload: AlimTalkAccountUpdatePayload = {
      profile_name: profileName.trim(),
      business_name: businessName.trim(),
      business_number: businessNumber.trim(),
      sender_no: senderNo.trim(),
      ...(sendProfileKey.trim() ? { send_profile_key: sendProfileKey.trim() } : {}),
    };

    setSaving(true);
    try {
      const response = await notificationCenterService.updateAlimTalkAccount(payload);
      applyAccount(response.data);
      toast.success("알림톡 연결 정보가 저장되었습니다.");
    } catch (error: any) {
      toast.error(getErrorMessage(error, "알림톡 연결 정보를 저장하지 못했습니다."));
    } finally {
      setSaving(false);
    }
  };

  const disconnectAccount = async () => {
    const confirmed = await dialogService.confirm({
      title: "지점 전용 알림톡 연결을 해제하시겠습니까?",
      description: "이 지점에 저장된 전용 발신 프로필 키 연결을 해제합니다. 다시 연결하기 전에는 지점 직접 알림톡을 발송할 수 없으며, 기존 발송 이력은 유지됩니다.",
      confirmLabel: "연결 해제",
      cancelLabel: "취소",
      tone: "destructive",
      destructive: true,
    });
    if (!confirmed) return;

    setDisconnecting(true);
    try {
      const response = await notificationCenterService.disconnectAlimTalkAccount();
      applyAccount(response.data);
      toast.success("지점 전용 알림톡 연결을 해제했습니다.");
    } catch (error: any) {
      toast.error(getErrorMessage(error, "알림톡 연결을 해제하지 못했습니다."));
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="border rounded p-4 text-center" role="status">
        <Spinner size="sm" className="me-2" />
        알림톡 연결 정보를 확인하고 있습니다.
      </div>
    );
  }

  if (loadError || !account) {
    return (
      <Alert color="danger" className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <span>{loadError || "알림톡 연결 정보를 불러오지 못했습니다."}</span>
        <Button color="outline-danger" size="sm" onClick={() => void loadAccount()}>
          다시 시도
        </Button>
      </Alert>
    );
  }

  const deliveryStatus = getDeliveryStatus(account);
  const mutating = saving || disconnecting;
  const hasExplicitSiteKey = !account.using_default && Boolean(account.sender_key_hint);
  const canDisconnect = hasExplicitSiteKey && !readOnly;
  const requiresSiteKey = !hasExplicitSiteKey;
  const senderKeyLabel = account.using_default
    ? "지점 전용 발신 프로필 키"
    : hasExplicitSiteKey
      ? "교체할 발신 프로필 키"
      : "새 발신 프로필 키";

  return (
    <div className="border rounded p-3 p-md-4">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h5 className="mb-1">지점 알림톡 연결</h5>
          <p className="text-muted mb-0">
            이 지점에서 사용할 발신 프로필과 사업자 정보를 관리합니다.
          </p>
        </div>
        <Badge color={deliveryStatus.color}>{deliveryStatus.label}</Badge>
      </div>

      <Alert color="info" className="mb-3">
        이미 발급받은 알림톡 발신 프로필 키를 이 지점에 연결하는 화면입니다. 새 발신 프로필 발급과
        카카오 검수·승인은 제공사 관리자 화면에서 진행해 주세요.
      </Alert>

      <Row className="g-2 mb-3">
        <Col md={4}>
          <div className="rounded border bg-light p-3 h-100">
            <div className="text-muted small mb-1">지점 연결</div>
            <strong>
              {account.using_default ? "공용 기본 프로필 사용" : hasExplicitSiteKey ? "지점 키 저장됨" : "연결 필요"}
            </strong>
          </div>
        </Col>
        <Col md={4}>
          <div className="rounded border bg-light p-3 h-100">
            <div className="text-muted small mb-1">알림톡 API</div>
            <strong>{account.provider_configured ? "설정됨" : "관리자 설정 필요"}</strong>
          </div>
        </Col>
        <Col md={4}>
          <div className="rounded border bg-light p-3 h-100">
            <div className="text-muted small mb-1">전송 가능 여부</div>
            <strong>{account.delivery_ready ? "전송 가능" : "전송 불가"}</strong>
          </div>
        </Col>
      </Row>

      {account.sender_key_hint ? (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="text-muted small">현재 발신 프로필 키</span>
          <code>{account.sender_key_hint}</code>
          {account.using_default ? <Badge color="secondary">공용 기본 프로필</Badge> : null}
        </div>
      ) : null}

      {account.errors?.length ? (
        <Alert color="warning" className="mb-3">
          <strong className="d-block mb-1">확인할 항목</strong>
          <ul className="mb-0 ps-3">
            {account.errors.map((error) => <li key={error}>{error}</li>)}
          </ul>
        </Alert>
      ) : null}

      {readOnly ? (
        <Alert color="light" className="mb-3">
          연결 정보 변경은 지점주 또는 마스터 관리자만 할 수 있습니다.
        </Alert>
      ) : null}

      <form onSubmit={saveAccount}>
        <Row className="g-3">
          <Col lg={6}>
            <Label htmlFor="alimtalk-send-profile-key">{senderKeyLabel}</Label>
            <Input
              id="alimtalk-send-profile-key"
              type="password"
              autoComplete="new-password"
              value={sendProfileKey}
              onChange={(event) => setSendProfileKey(event.target.value)}
              placeholder={requiresSiteKey ? "이 지점에 발급된 키 입력" : "교체할 때만 입력"}
              maxLength={40}
              disabled={mutating || readOnly}
              required={requiresSiteKey && !readOnly}
              aria-describedby="alimtalk-send-profile-key-help"
            />
            <div id="alimtalk-send-profile-key-help" className="form-text">
              저장된 키 원문은 다시 표시되지 않습니다.
            </div>
          </Col>
          <Col lg={6}>
            <Label htmlFor="alimtalk-profile-name">프로필명</Label>
            <Input
              id="alimtalk-profile-name"
              value={profileName}
              onChange={(event) => setProfileName(event.target.value)}
              placeholder="고객에게 식별할 수 있는 프로필명"
              disabled={mutating || readOnly}
              required
            />
          </Col>
          <Col lg={6}>
            <Label htmlFor="alimtalk-business-name">사업자명</Label>
            <Input
              id="alimtalk-business-name"
              value={businessName}
              onChange={(event) => setBusinessName(event.target.value)}
              placeholder="사업자명"
              disabled={mutating || readOnly}
              required
            />
          </Col>
          <Col lg={6}>
            <Label htmlFor="alimtalk-business-number">사업자등록번호</Label>
            <Input
              id="alimtalk-business-number"
              value={businessNumber}
              onChange={(event) => setBusinessNumber(event.target.value)}
              placeholder="123-45-67890"
              disabled={mutating || readOnly}
            />
          </Col>
          <Col lg={6}>
            <Label htmlFor="alimtalk-sender-no">DK 등록 발신번호</Label>
            <Input
              id="alimtalk-sender-no"
              type="tel"
              autoComplete="tel"
              value={senderNo}
              onChange={(event) => setSenderNo(event.target.value)}
              placeholder="예: 02-1234-5678"
              maxLength={24}
              disabled={mutating || readOnly}
              required={!readOnly}
              aria-describedby="alimtalk-sender-no-help"
            />
            <div id="alimtalk-sender-no-help" className="form-text">
              DK 발신프로필에 등록된 번호와 동일하게 입력해 주세요.
            </div>
          </Col>
        </Row>

        <div className="d-flex flex-wrap justify-content-between gap-2 mt-4">
          <div>
            {canDisconnect ? (
              <Button
                color="outline-danger"
                type="button"
                onClick={() => void disconnectAccount()}
                disabled={mutating}
              >
                {disconnecting ? <><Spinner size="sm" className="me-2" />해제 중</> : "연결 해제"}
              </Button>
            ) : null}
          </div>
          {!readOnly ? (
            <Button color="primary" type="submit" disabled={mutating}>
              {saving ? <><Spinner size="sm" className="me-2" />저장 중</> : "연결 정보 저장"}
            </Button>
          ) : null}
        </div>
      </form>
    </div>
  );
}
